import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, ChevronDown, Command, Server, Smartphone, Globe } from "lucide-react";
import { cn } from "@/lib/cn";
import { ThemeToggle } from "@/ui";
import { useWorkspace } from "./WorkspaceContext";
import { useAskNara } from "./AskNara";

/** The coral dot mark — sacred (design.md). Shown app-wide; wordmark only on Today. */
function DotMark() {
  return <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" aria-hidden />;
}

function PropertySwitcher() {
  const { properties, current, setCurrent } = useWorkspace();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-hairline px-3 py-1.5 text-[13px] text-ink transition-colors hover:bg-paper-2 dark:hover:bg-white/5"
      >
        <span className="h-2 w-2 rounded-full" style={{ background: current.hue }} />
        <span className="max-w-[120px] truncate font-medium">{current.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted" strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-card border border-hairline bg-paper p-1 shadow-md animate-fade-up">
            {properties.map((p) => {
              const active = p.id === current.id;
              const Icon =
                p.kind === "ios" || p.kind === "android"
                  ? Smartphone
                  : p.kind === "server"
                    ? Server
                    : Globe;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    setCurrent(p.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-[7px] px-2.5 py-2 text-left transition-colors hover:bg-paper-2 dark:hover:bg-white/5"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: p.hue }} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink">{p.name}</span>
                    <span className="block truncate font-mono text-[11px] text-subtle">{p.domain}</span>
                  </span>
                  <Icon className="h-3.5 w-3.5 text-subtle" strokeWidth={1.5} />
                  {active && <Check className="h-4 w-4 text-accent" strokeWidth={2} />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function Topbar() {
  const loc = useLocation();
  const onToday = loc.pathname.startsWith("/today") || loc.pathname.startsWith("/home");
  const { setOpen } = useAskNara();

  return (
    <header className="sticky top-0 z-40 border-b border-hairline bg-canvas/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4 md:h-16 md:px-6">
        <div className="flex items-center gap-2">
          <DotMark />
          {onToday ? (
            <span className="text-[16px] font-semibold tracking-tight text-ink-strong">
              aigen <span className="font-normal text-muted">Studio</span>
            </span>
          ) : (
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">Studio</span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <PropertySwitcher />
          <button
            onClick={() => setOpen(true)}
            className={cn(
              "hidden items-center gap-2 rounded-full border border-hairline px-3 py-1.5 text-[13px] text-muted transition-colors hover:text-ink sm:flex",
            )}
          >
            <span>Ask Nara</span>
            <kbd className="flex items-center gap-0.5 rounded bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] text-subtle dark:bg-white/5">
              <Command className="h-3 w-3" />K
            </kbd>
          </button>
          <ThemeToggle compact />
        </div>
      </div>
    </header>
  );
}
